import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { SelectNative } from "../components/ui/select-native";
import { Disclaimer } from "../components/Disclaimer";
import { useAssumptions } from "../context/AssumptionsContext";
import { useProfile } from "../context/ProfileContext";
import { formatINRCompact, formatPercent } from "../lib/currency";
import { CurrencyHint } from "../components/CurrencyHint";
import { representativeAnnualIncome } from "../lib/profileOptions";
import { downloadCalculatorPDF, createScenario } from "../lib/api";

const DONUT_COLORS = ["var(--gold)", "var(--gold-deep)", "var(--muted)", "var(--gold-bright)"];

// Extracted so React doesn't allocate a fresh object on every render (perf nit surfaced by review).
const TOOLTIP_STYLE = {
  background: "var(--elevated)",
  border: "1px solid var(--border)",
};

/** Build a zod schema from a config's field list. Numeric fields are coerced + bounded;
 * select fields must be one of their option values; text fields are non-empty strings. */
function buildSchema(fields) {
  const shape = {};
  for (const field of fields) {
    if (field.type === "select") {
      const values = field.options.map((o) => o.value);
      shape[field.key] = z.enum(values);
    } else if (field.type === "text") {
      shape[field.key] = field.optional ? z.string().optional() : z.string().min(1, "Required");
    } else {
      let rule = z.coerce.number({ invalid_type_error: `${field.label} must be a number` });
      if (field.min !== undefined) rule = rule.min(field.min, `Minimum is ${field.min}`);
      if (field.max !== undefined) rule = rule.max(field.max, `Maximum is ${field.max}`);
      shape[field.key] = field.optional ? rule.optional() : rule;
    }
  }
  return z.object(shape);
}

/** Resolve a field's `profileKey` (Task 10 "pre-fill matching calculator inputs from the
 * profile") to a value from the member's saved profile, or undefined if that profile field
 * isn't set. New mappings go here as more calculators adopt `profileKey`. */
function resolveProfileValue(profileKey, profile) {
  if (profileKey === "annualIncome") return representativeAnnualIncome(profile.income_range);
  if (profileKey === "netWorth") return profile.approx_net_worth;
  return undefined;
}

function defaultValuesFor(config, assumptions, profile) {
  const values = {};
  for (const field of config.fields) {
    if (field.profileKey && profile) {
      const profileValue = resolveProfileValue(field.profileKey, profile);
      if (profileValue !== undefined) {
        values[field.key] = profileValue;
        continue;
      }
    }
    if (field.assumptionKey && assumptions[field.assumptionKey] !== undefined) {
      // Assumptions are stored as decimals (0.12); percent fields display whole numbers (12).
      // Round to 2 decimals to kill floating-point drift (0.07 * 100 = 7.000000000000001)
      // that would otherwise surface a ridiculous input like "7.000000000000001%" to users.
      if (field.type === "percent") {
        values[field.key] = Math.round(assumptions[field.assumptionKey] * 10000) / 100;
      } else {
        values[field.key] = assumptions[field.assumptionKey];
      }
    } else {
      values[field.key] = field.default;
    }
  }
  return values;
}

/** Format a headline/breakdown result item: explicit `display` string wins, else `format`
 * ("currency" default, "percent", "months", or "text") formats `value`. */
function formatResultValue(item) {
  if (item.display !== undefined) return item.display;
  const format = item.format || "currency";
  if (format === "percent") return formatPercent(item.value);
  if (format === "months") {
    if (item.value === null || item.value === undefined) return "Never runs out";
    const years = Math.floor(item.value / 12);
    const months = Math.round(item.value % 12);
    return years > 0 ? `${years}y ${months}m` : `${months}m`;
  }
  if (format === "text") return String(item.value);
  return formatINRCompact(item.value);
}

/** Format a raw form value for the PDF's "Inputs" table, honouring the field type. */
function formatFieldValue(field, rawValue) {
  if (field.type === "percent") {
    // Always 2 decimals in the PDF for consistency: 7 → 7.00%, 7.25 → 7.25%, 7.256 → 7.26%.
    const n = Number(rawValue);
    if (!Number.isFinite(n)) return `${rawValue}%`;
    return `${n.toFixed(2)}%`;
  }
  if (field.type === "currency") return formatINRCompact(Number(rawValue));
  if (field.type === "select") {
    const opt = field.options.find((o) => o.value === rawValue);
    return opt ? opt.label : String(rawValue);
  }
  return String(rawValue);
}

function ResultChart({ config, result }) {
  const { chart } = config;
  if (!chart || !result.series || result.series.length === 0) return null;

  if (chart.type === "donut") {
    return (
      <div className="h-56 w-full" data-testid="calculator-chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={result.series}
              dataKey={chart.valueKey || "value"}
              nameKey={chart.nameKey || "name"}
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={2}
            >
              {result.series.map((entry, idx) => (
                <Cell key={`donut-${entry.name || idx}`} fill={DONUT_COLORS[idx % DONUT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => formatPercent(v)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chart.type === "bar") {
    return (
      <div className="h-56 w-full" data-testid="calculator-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={result.series}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey={chart.xKey} stroke="var(--muted)" fontSize={12} />
            <YAxis
              stroke="var(--muted)"
              fontSize={12}
              tickFormatter={(v) => formatINRCompact(v)}
              width={70}
            />
            <Tooltip formatter={(v) => formatINRCompact(v)} />
            {chart.series.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.label} fill={`var(${s.colorVar || "--gold"})`} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // default: line
  return (
    <div className="h-56 w-full" data-testid="calculator-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={result.series}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey={chart.xKey} stroke="var(--muted)" fontSize={12} />
          <YAxis
            stroke="var(--muted)"
            fontSize={12}
            tickFormatter={(v) => formatINRCompact(v)}
            width={70}
          />
          <Tooltip
            formatter={(v) => formatINRCompact(v)}
            contentStyle={TOOLTIP_STYLE}
          />
          {chart.series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={`var(${s.colorVar || "--gold"})`}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Optional year-by-year (or period-by-period) schedule table. A config's compute() can return
 * `result.table = { title?, columns: [{key,label,format?}], rows: [{...}] }`. Used by the Task 16.1
 * comprehensive views to show a full projection alongside the headline + chart. Scrolls on small
 * screens so it never forces the page body to scroll horizontally. */
function ScheduleTable({ table }) {
  if (!table || !table.columns || !table.rows || table.rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      {table.title && <p className="text-sm font-medium text-text">{table.title}</p>}
      <div className="max-h-72 overflow-auto rounded-md border border-border" data-testid="schedule-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-elevated">
            <tr>
              {table.columns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left font-medium text-muted">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, idx) => {
              // Prefer any explicit id/period/year column value for stable keys; fall back to
              // a hash of the first column so rows survive re-ordering.
              const firstColKey = table.columns[0]?.key;
              const rowKey = row.id ?? row.year ?? row.period ?? `${firstColKey}-${row[firstColKey] ?? idx}`;
              return (
                <tr key={`schedule-${rowKey}`} className="border-t border-border">
                  {table.columns.map((col) => (
                    <td key={col.key} className="px-3 py-1.5 text-text">
                      {formatResultValue({ value: row[col.key], format: col.format })}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Generic calculator renderer — CLAUDE.md architecture rule. Given ANY calculator config, this
 * produces: a mobile-first form (react-hook-form + zod), live results on every change, a
 * recharts chart, the disclaimer, a "Download PDF" button (Task 6), and a save-scenario button.
 * Calculators never get their own page component — they get a config.
 */
export function CalculatorRenderer({ config, prefillInputs }) {
  const { assumptions } = useAssumptions();
  const { profile } = useProfile();
  const schema = useMemo(() => buildSchema(config.fields), [config.fields]);
  const [downloading, setDownloading] = useState(false);
  const [savingLabel, setSavingLabel] = useState(null); // null = not showing the save form
  const [saving, setSaving] = useState(false);
  const appliedPrefill = useRef(false);

  const {
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: defaultValuesFor(config, assumptions, profile),
  });

  const values = watch();

  // Re-running a saved scenario (from My Scenarios) always wins over profile-derived defaults.
  useEffect(() => {
    if (prefillInputs && !appliedPrefill.current) {
      reset(prefillInputs);
      appliedPrefill.current = true;
    }
  }, [prefillInputs, reset]);

  // Once the member's profile loads, re-fill any field whose `profileKey` matches — unless a
  // saved scenario was just re-run into this form instead.
  useEffect(() => {
    if (profile && !appliedPrefill.current) {
      reset(defaultValuesFor(config, assumptions, profile));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const result = useMemo(() => {
    if (Object.keys(errors).length > 0) return null;
    try {
      return config.compute(values, assumptions);
    } catch {
      return null;
    }
  }, [values, errors, config, assumptions]);

  function handlePickSavedGoal(e) {
    const idx = e.target.value;
    if (idx === "" || !profile) return;
    const goal = [...profile.short_term_goals, ...profile.long_term_goals][Number(idx)];
    if (!goal) return;
    const currentYear = new Date().getFullYear();
    reset({
      ...values,
      goalName: goal.name,
      presentCost: goal.target_amount,
      yearsToGoal: Math.max(goal.target_year - currentYear, 1),
    });
  }

  async function handleConfirmSaveScenario() {
    if (!result || !savingLabel.trim()) return;
    setSaving(true);
    try {
      await createScenario({
        calculator_key: config.key,
        label: savingLabel.trim(),
        inputs: values,
        outputs: {
          headline: result.headline,
          breakdown: result.breakdown || [],
          extraHeadlines: result.extraHeadlines || [],
        },
        assumptions,
      });
      toast.success("Scenario saved — find it under My Scenarios.");
      setSavingLabel(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save this scenario.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDownloadPDF() {
    if (!result) return;
    setDownloading(true);
    try {
      // Only line/bar series make sense as a numeric x-axis chart in the PDF; skip donuts.
      const chartableSeries = config.chart && config.chart.type !== "donut" ? result.series || [] : [];
      // Fold extra headlines (corpus built, projected SIP, alternate FIRE targets, etc.) into the
      // breakdown so the PDF surfaces them right below the primary headline. Without this the
      // PDF was silently dropping key numbers users can see on-screen — see 21-Feb-2026 fixes.
      const extras = (result.extraHeadlines || []).map((item) => ({
        label: item.label,
        value: formatResultValue(item),
      }));
      const breakdown = (result.breakdown || []).map((item) => ({
        label: item.label,
        value: formatResultValue(item),
      }));
      await downloadCalculatorPDF({
        calculator_title: config.title,
        inputs: config.fields.map((field) => ({
          label: field.label,
          value: formatFieldValue(field, values[field.key]),
        })),
        headline: { label: result.headline.label, value: formatResultValue(result.headline) },
        breakdown: [...extras, ...breakdown],
        series: chartableSeries,
      });
    } catch (err) {
      toast.error("Couldn't generate the PDF. Is the backend running?");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{config.title}</CardTitle>
          <CardDescription>{config.description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {config.notice && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
              {config.notice}
            </div>
          )}
          {config.goalPicker && profile && (profile.short_term_goals.length > 0 || profile.long_term_goals.length > 0) && (
            <div className="flex flex-col gap-1.5 border-b border-border pb-4">
              <Label htmlFor="saved-goal-picker">Use a saved goal</Label>
              <SelectNative id="saved-goal-picker" defaultValue="" onChange={handlePickSavedGoal}>
                <option value="">— Select a saved goal —</option>
                {[...profile.short_term_goals, ...profile.long_term_goals].map((goal, idx) => (
                  <option key={`goal-${goal.name}-${goal.target_year}-${idx}`} value={idx}>
                    {goal.name} ({formatINRCompact(goal.target_amount)} by {goal.target_year})
                  </option>
                ))}
              </SelectNative>
            </div>
          )}
          {config.fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <Label htmlFor={field.key}>
                {field.label}
                {field.type === "percent" ? " (%)" : ""}
                {field.optional ? " (optional)" : ""}
              </Label>
              {field.type === "select" ? (
                <SelectNative id={field.key} {...register(field.key)}>
                  {field.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectNative>
              ) : (
                <Input
                  id={field.key}
                  type={field.type === "text" ? "text" : "number"}
                  step="any"
                  {...register(field.key)}
                />
              )}
              {/* For currency inputs, render the "One Lakh Twenty-Five Thousand" hint so users
                  catch a missing/extra zero at a glance. Values are watched from RHF. */}
              {field.type === "currency" && <CurrencyHint value={values[field.key]} />}
              {field.help && <p className="text-xs text-muted">{field.help}</p>}
              {errors[field.key] && (
                <p className="text-xs text-red-400">{errors[field.key].message}</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Results</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {result ? (
            <>
              <div>
                <p className="text-sm text-muted">{result.headline.label}</p>
                <p className="text-3xl font-semibold text-gold">
                  {formatResultValue(result.headline)}
                </p>
                {result.note && <p className="mt-1 text-xs text-muted">{result.note}</p>}
              </div>

              {result.extraHeadlines && result.extraHeadlines.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {result.extraHeadlines.map((item) => (
                    <div key={item.label} className="rounded-md border border-border p-2">
                      <p className="text-xs text-muted">{item.label}</p>
                      <p className="text-base font-semibold text-text">
                        {formatResultValue(item)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {result.breakdown && result.breakdown.length > 0 && (
                <div className="flex flex-col gap-1">
                  {result.breakdown.map((item) => (
                    <div key={item.label} className="flex justify-between text-sm">
                      <span className="text-muted">{item.label}</span>
                      <span className="font-medium">{formatResultValue(item)}</span>
                    </div>
                  ))}
                </div>
              )}

              <ResultChart config={config} result={result} />

              {result.table && <ScheduleTable table={result.table} />}

              <div className="mt-2 flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  className="flex-1"
                >
                  {downloading ? "Generating…" : "Download PDF"}
                </Button>
                {savingLabel === null && (
                  <Button variant="outline" onClick={() => setSavingLabel(`${config.title} — ${new Date().toLocaleDateString()}`)} className="flex-1">
                    Save this scenario
                  </Button>
                )}
              </div>

              {savingLabel !== null && (
                <div className="flex flex-col gap-2 rounded-md border border-border p-3">
                  <Label htmlFor="scenario-label">Name this scenario</Label>
                  <div className="flex gap-2">
                    <Input
                      id="scenario-label"
                      value={savingLabel}
                      onChange={(e) => setSavingLabel(e.target.value)}
                      autoFocus
                    />
                    <Button onClick={handleConfirmSaveScenario} disabled={saving || !savingLabel.trim()}>
                      {saving ? "Saving…" : "Confirm"}
                    </Button>
                    <Button variant="ghost" onClick={() => setSavingLabel(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <Disclaimer />
            </>
          ) : (
            <p className="text-sm text-muted">Enter valid values to see results.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { buildSchema, defaultValuesFor, formatResultValue, formatFieldValue };
